import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { SlidersHorizontal } from "lucide-react";
import { api, fmtCAD } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { ProductCard } from "@/components/ProductCard";

const LIMIT = 48;

export default function Shop() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [subs, setSubs] = useState([]);
  const [bounds, setBounds] = useState({ min: 0, max: 0 });
  const [range, setRange] = useState(null);
  const [data, setData] = useState({ products: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const category = params.get("category") || "";
  const brand = params.get("brand") || "";
  const subtype = params.get("subtype") || "";
  const search = params.get("search") || "";
  const sort = params.get("sort") || "featured";
  const page = parseInt(params.get("page") || "1");
  const minPrice = params.get("min_price");
  const maxPrice = params.get("max_price");

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  // subtypes + price bounds depend on category/brand
  useEffect(() => {
    api.get("/subtypes", { params: { category, brand } }).then((r) => setSubs(r.data)).catch(() => setSubs([]));
    api.get("/price-range", { params: { category, brand } }).then((r) => {
      setBounds(r.data);
      setRange([Number(minPrice) || r.data.min, Number(maxPrice) || r.data.max]);
    }).catch(() => {});
  }, [category, brand]); // eslint-disable-line

  const fetchProducts = useCallback(() => {
    setLoading(true);
    const p = { category, brand, subtype, search, sort, page, limit: LIMIT };
    if (minPrice) p.min_price = minPrice;
    if (maxPrice) p.max_price = maxPrice;
    api.get("/products", { params: p }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [category, brand, subtype, search, sort, page, minPrice, maxPrice]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const setParam = (key, val) => {
    const p = new URLSearchParams(params);
    if (val) p.set(key, val); else p.delete(key);
    // choosing a category deselects the brand (and vice-versa) + reset subtype + price
    if (key === "category") { p.delete("brand"); p.delete("subtype"); p.delete("min_price"); p.delete("max_price"); }
    if (key === "brand") { p.delete("category"); p.delete("subtype"); p.delete("min_price"); p.delete("max_price"); }
    if (key !== "page") p.delete("page");
    setParams(p);
  };

  const applyPrice = (vals) => {
    const p = new URLSearchParams(params);
    if (vals[0] > bounds.min) p.set("min_price", String(vals[0])); else p.delete("min_price");
    if (vals[1] < bounds.max) p.set("max_price", String(vals[1])); else p.delete("max_price");
    p.delete("page");
    setParams(p);
  };

  const totalPages = Math.ceil(data.total / LIMIT);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10">
      <div className="mb-8">
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">{t("shop.title")}</h1>
        <p className="text-gray-500 mt-2">{data.total} {t("shop.products")}{brand && ` · ${brand}`}{subtype && ` · ${subtype}`}{search && ` · « ${search} »`}</p>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-10">
        <aside className="lg:sticky lg:top-28 self-start space-y-8">
          <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-gray-500">
            <SlidersHorizontal size={16} /> {t("shop.filters")}
          </div>
          {bounds.max > bounds.min && range && (
            <div data-testid="price-filter">
              <h3 className="font-semibold mb-4">{t("shop.price")}</h3>
              <SliderPrimitive.Root
                className="relative flex w-full touch-none select-none items-center py-2"
                min={bounds.min} max={bounds.max} step={Math.max(1, Math.round((bounds.max - bounds.min) / 100))}
                value={range} onValueChange={setRange} onValueCommit={applyPrice} minStepsBetweenThumbs={1}>
                <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-gray-200">
                  <SliderPrimitive.Range className="absolute h-full bg-brand-red" />
                </SliderPrimitive.Track>
                <SliderPrimitive.Thumb className="block h-4 w-4 rounded-full border-2 border-brand-red bg-white shadow focus-visible:outline-none" aria-label="Prix min" />
                <SliderPrimitive.Thumb className="block h-4 w-4 rounded-full border-2 border-brand-red bg-white shadow focus-visible:outline-none" aria-label="Prix max" />
              </SliderPrimitive.Root>
              <div className="mt-3 flex items-center justify-between text-sm text-gray-600" data-testid="price-range-label">
                <span>{fmtCAD(range[0])}</span><span className="text-gray-400 px-2">–</span><span>{fmtCAD(range[1])}</span>
              </div>
            </div>
          )}
          <div>
            <h3 className="font-semibold mb-3">{t("shop.brand")}</h3>
            <div className="space-y-2">
              <button onClick={() => setParam("brand", "")} className={`block text-sm ${!brand ? "text-brand-red font-semibold" : "text-gray-600"}`} data-testid="filter-brand-all">{t("shop.allBrands")}</button>
              {brands.map((b) => (
                <button key={b.name} onClick={() => setParam("brand", b.name)} className={`block text-sm ${brand === b.name ? "text-brand-red font-semibold" : "text-gray-600 hover:text-black"}`} data-testid={`filter-brand-${b.name}`}>
                  {b.name} <span className="text-gray-400">({b.count})</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <h3 className="font-semibold mb-3">{t("shop.category")}</h3>
            <div className="space-y-2">
              <button onClick={() => setParam("category", "")} className={`block text-sm ${!category ? "text-brand-red font-semibold" : "text-gray-600"}`} data-testid="filter-cat-all">{t("shop.allCats")}</button>
              {cats.map((c) => (
                <button key={c.key} onClick={() => setParam("category", c.key)} className={`block text-sm ${category === c.key ? "text-brand-red font-semibold" : "text-gray-600 hover:text-black"}`} data-testid={`filter-cat-${c.key}`}>
                  {c.label} <span className="text-gray-400">({c.count})</span>
                </button>
              ))}
            </div>
          </div>
          {subs.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3">{t("shop.subtype")}</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                <button onClick={() => setParam("subtype", "")} className={`block text-sm ${!subtype ? "text-brand-red font-semibold" : "text-gray-600"}`} data-testid="filter-subtype-all">{t("shop.allSubtypes")}</button>
                {subs.map((s) => (
                  <button key={s.name} onClick={() => setParam("subtype", s.name)} className={`block text-sm ${subtype === s.name ? "text-brand-red font-semibold" : "text-gray-600 hover:text-black"}`} data-testid={`filter-subtype-${s.name}`}>
                    {s.name} <span className="text-gray-400">({s.count})</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </aside>

        <div>
          <div className="flex justify-end mb-5">
            <select value={sort} onChange={(e) => setParam("sort", e.target.value)} className="border border-gray-300 px-3 py-2 text-sm rounded-sm outline-none focus:border-brand-red" data-testid="sort-select">
              <option value="featured">{t("shop.sortFeatured")}</option>
              <option value="price_asc">{t("shop.sortPriceAsc")}</option>
              <option value="price_desc">{t("shop.sortPriceDesc")}</option>
              <option value="name">{t("shop.sortName")}</option>
            </select>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {Array(9).fill(0).map((_, i) => <div key={i} className="border border-gray-200 bg-white aspect-[3/4] animate-pulse" />)}
            </div>
          ) : data.products.length === 0 ? (
            <div className="py-24 text-center text-gray-500" data-testid="no-products">{t("shop.none")}</div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="product-grid">
              {data.products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-12 flex-wrap">
              <button disabled={page <= 1} onClick={() => setParam("page", String(page - 1))} className="h-10 px-3 border border-gray-300 text-sm font-medium disabled:opacity-40 hover:border-brand-red" data-testid="page-prev">‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((n) => n === 1 || n === totalPages || Math.abs(n - page) <= 2)
                .reduce((acc, n) => { if (acc.length && n - acc[acc.length - 1] > 1) acc.push("..."); acc.push(n); return acc; }, [])
                .map((n, idx) => n === "..." ? (
                  <span key={`e${idx}`} className="px-2 text-gray-400">…</span>
                ) : (
                  <button key={n} onClick={() => setParam("page", String(n))} className={`h-10 w-10 border text-sm font-medium ${page === n ? "bg-brand-red text-white border-brand-red" : "border-gray-300 hover:border-brand-red"}`} data-testid={`page-${n}`}>{n}</button>
                ))}
              <button disabled={page >= totalPages} onClick={() => setParam("page", String(page + 1))} className="h-10 px-3 border border-gray-300 text-sm font-medium disabled:opacity-40 hover:border-brand-red" data-testid="page-next">›</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
